import React, { useEffect, useMemo, useState } from 'react';
import styles from '../../styles/ReporteSegurosEstudiantesStyles/ReporteSegurosEstudiantes.module.css';
import { exportReporteSegurosXLS } from '../../utils/exportReporteSegurosXLS';

const PAGE_SIZE = 20;

/**
 * Tabla del Reporte de Seguros de Estudiantes.
 *
 * Props:
 *  - resultados: Array<row>
 *  - contexto: { ciclo, campus, seccion, programa }
 */
function TablaReporteSeguros({ resultados = [], contexto = {} }) {
  const [page, setPage] = useState(1);

  const total = resultados.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => { setPage(1); }, [resultados]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return resultados.slice(start, start + PAGE_SIZE);
  }, [resultados, page]);

  const handleExport = () => {
    exportReporteSegurosXLS({ resultados, contexto });
  };

  const formatFecha = (iso) => {
    if (!iso) return '—';
    // YYYY-MM-DD → DD/MM/YYYY (sin TZ shift)
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return iso;
  };

  return (
    <>
      <div className={styles.reportHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h2 className={styles.reportTitle}>REPORTE DE SEGUROS DE ESTUDIANTES</h2>
        <button
          type="button"
          className={styles.generateButton}
          onClick={handleExport}
          disabled={total === 0}
          style={{ padding: '8px 16px', fontSize: 13 }}
        >
          📊 Exportar a Excel
        </button>
      </div>

      <div className={styles.reportMeta}>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Campus:</span>
          <span>{contexto.campus || 'Todos'}</span>
        </div>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Sección:</span>
          <span>{contexto.seccion || 'Todas'}</span>
        </div>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Programa:</span>
          <span>{contexto.programa || 'Todos'}</span>
        </div>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Total estudiantes:</span>
          <span>{total}</span>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.reportTable}>
          <thead>
            <tr>
              <th className={styles.cellCenter} style={{ width: 50 }}>#</th>
              <th>Ciclo Escolar</th>
              <th>Matrícula</th>
              <th>Nombre</th>
              <th>Género</th>
              <th>Programa</th>
              <th>Fecha de Nacimiento</th>
              <th className={styles.cellCenter}>Edad</th>
              <th className={styles.cellCenter}>Tiene Seguro</th>
              <th>Fecha de Seguro</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan={10} className={styles.cellCenter} style={{ padding: 20, color: '#999' }}>
                  No hay registros para mostrar.
                </td>
              </tr>
            ) : (
              pageRows.map((r, idx) => {
                const consecutivo = (page - 1) * PAGE_SIZE + idx + 1;
                return (
                  <tr key={`${r.id_estudiante}-${idx}`}>
                    <td className={styles.cellCenter}>{consecutivo}</td>
                    <td>{r.ciclo_escolar || '—'}</td>
                    <td>{r.matricula || '—'}</td>
                    <td>{r.nombre || '—'}</td>
                    <td>{r.genero || '—'}</td>
                    <td>{r.programa || '—'}</td>
                    <td>{formatFecha(r.fecha_nacimiento)}</td>
                    <td className={styles.cellCenter}>{r.edad ?? '—'}</td>
                    <td className={styles.cellCenter}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          background: r.tiene_seguro === 'SI' ? '#e6f4ea' : '#fdecea',
                          color: r.tiene_seguro === 'SI' ? '#1e7e34' : '#c5221f',
                        }}
                      >
                        {r.tiene_seguro || 'NO'}
                      </span>
                    </td>
                    <td>{r.tiene_seguro === 'SI' ? formatFecha(r.fecha_seguro) : '—'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className={styles.pagination}>
          <span className={styles.pageInfo}>
            Mostrando {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} de {total}
          </span>
          <div className={styles.pageButtons}>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              « Anterior
            </button>
            <span className={styles.pageInfo}>Página {page} de {totalPages}</span>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Siguiente »
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default TablaReporteSeguros;
